import { css, html, nothing } from "lit";
import { fallbackLabels, type LabelTable, type Labels } from "../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressAudio, pressFeedback } from "../chrome/ChromeElement";
import { artImage, type ChromeArt } from "../chrome/chromeArt";
import { chromeControlStyles } from "../chrome/chromeStyles";
import { fitTexts } from "../chrome/fitText";
import type { AutoSpinPopupBindings, AutoSpinPopupPort } from "./AutoSpinPorts";
import { fitScale } from "@slot/presentation";

export const AUTOSPIN_POPUP_LABELS = { title: ["autospin.title", "Autospins"], start: ["autospin.start", "Start Autospins"] } as const satisfies LabelTable;
export type AutoSpinPopupLabels = Labels<typeof AUTOSPIN_POPUP_LABELS>;

/** The desktop popover (755.4×529.6, no backdrop) and the mobile overlay panel (980×607.7, max 1080 wide; 2000 in landscape). */
const DESKTOP = { width: 755.4, height: 529.6 } as const;
const MOBILE = { width: 980, height: 607.7, maxWidth: 1080, landscapeMaxWidth: 2000, close: { size: 100, x: 387.5, yUp: 303.85 - 102.25 - 3.613 }, backdropAlpha: 198 / 255 } as const;
/** `AutoSpinPopupContent.prefab`: title top 83.15 (125.5 tall), the options grid mid, the start button bottom 67.38 (its label SHRINK, no wrap: a long translation scales down to one line). */
const CONTENT = { titleTop: 83.15, titleHeight: 125.5, titleSize: 68.3, titleInset: 158.8, gridHeight: 200.3, gridInset: 50, option: { width: 149.3, height: 80.32, gap: 10, size: 65.8 }, start: { width: 479.4, height: 86.88, bottom: 67.38, size: 59.3 } } as const;

export const AUTOSPIN_POPUP_TAG = "slot-autospin-popup";

/**
 * `<slot-autospin-popup>`: the autospin picker. On desktop a popover above the bar (positioned by
 * the bar; a press outside it reports `onOutsideClick`); on mobile a full-screen backdrop with the
 * scaled panel and a close button. The preset buttons, the selection and Start are one port.
 */
export class AutoSpinPopupElement extends ChromeElement implements AutoSpinPopupPort {
  public static override properties = {
    ...ChromeElement.properties,
    art: { attribute: false },
    labels: { attribute: false },
    open: { type: Boolean, reflect: true },
    counts: { state: true },
    selected: { state: true },
    optionsEnabled: { state: true },
    startEnabled: { state: true },
  };

  public declare art: ChromeArt;
  public declare labels: AutoSpinPopupLabels;
  public declare open: boolean;
  /** Desktop: the toggle that opened the popover, excluded from outside-click dismissal. */
  public anchor: EventTarget | null = null;
  private declare counts: number[];
  private declare selected: number | null;
  private declare optionsEnabled: boolean;
  private declare startEnabled: boolean;
  private bindings: AutoSpinPopupBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.labels = fallbackLabels(AUTOSPIN_POPUP_LABELS);
    this.open = false;
    this.counts = [];
    this.selected = null;
    this.optionsEnabled = true;
    this.startEnabled = false;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: none; color: #fff; font-weight: 500; }
      :host([open]) { display: block; }
      .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${Number(MOBILE.backdropAlpha.toFixed(3))}); pointer-events: auto; }
      .panel { position: absolute; pointer-events: auto; }
      .close { position: absolute; width: ${MOBILE.close.size}px; height: ${MOBILE.close.size}px; left: calc(50% + ${MOBILE.close.x}px); top: calc(50% - ${MOBILE.close.yUp}px); transform: translate(-50%, -50%); }
      .close:hover { filter: brightness(0.39); }
      .close:active { filter: brightness(0.73); }
      .title { position: absolute; left: ${CONTENT.titleInset}px; right: ${CONTENT.titleInset}px; top: ${CONTENT.titleTop}px; height: ${CONTENT.titleHeight}px; display: flex; align-items: center; justify-content: center; font-size: ${CONTENT.titleSize}px; }
      .grid { position: absolute; left: ${CONTENT.gridInset}px; right: ${CONTENT.gridInset}px; top: 50%; height: ${CONTENT.gridHeight}px; transform: translateY(-50%); display: flex; flex-wrap: wrap; align-content: flex-start; gap: ${CONTENT.option.gap}px; }
      .option { position: relative; width: ${CONTENT.option.width}px; height: ${CONTENT.option.height}px; font-size: ${CONTENT.option.size}px; display: flex; align-items: center; justify-content: center; }
      .option .bg { position: absolute; inset: 0; }
      .option span { position: relative; }
      .option[disabled] { filter: brightness(0.6); }
      .start { position: absolute; left: 50%; bottom: ${CONTENT.start.bottom}px; width: ${CONTENT.start.width}px; height: ${CONTENT.start.height}px; transform: translateX(-50%); border-radius: 8px; background: #fc8404; font-size: ${CONTENT.start.size}px; display: flex; align-items: center; justify-content: center; }
      .start:hover { filter: brightness(0.83); }
      .start:active { filter: none; }
      .start[disabled] { filter: brightness(0.49); }
    `,
  ];

  public bind(bindings: AutoSpinPopupBindings): void {
    this.bindings = bindings;
  }

  public setOptions(counts: readonly number[]): void {
    this.counts = [...counts];
  }

  public setSelected(count: number | null): void {
    this.selected = count;
  }

  public setOptionsInteractable(interactable: boolean): void {
    this.optionsEnabled = interactable;
  }

  public setStartInteractable(interactable: boolean): void {
    this.startEnabled = interactable;
  }

  public show(): void {
    this.open = true;
  }

  public hide(): void {
    this.open = false;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
    this.syncOutsideClick(this.open, this.anchor, () => this.bindings?.onOutsideClick());
  }

  private content() {
    return html`
      <div class="title" data-part="title"><span data-fit>${this.labels.title}</span></div>
      <div class="grid">
        ${this.counts.map(
          (count) => html`<button class="option" type="button" data-part="option" data-count=${count} ?disabled=${!this.optionsEnabled} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onSelect(count)}>
            <div class="bg art" style=${artImage(this.art, this.selected === count ? "autoAmountBgBrown" : "autoAmountBg")}></div>
            <span class="outline shadow">${count}</span>
          </button>`,
        )}
      </div>
      <button class="start outline shadow" type="button" data-part="start" ?disabled=${!this.startEnabled} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onStart()}><span data-fit>${this.labels.start}</span></button>
    `;
  }

  protected override render() {
    if (this.isDesktop) {
      return html`<div class="panel art" data-part="panel" style="left: 0; top: 0; width: ${DESKTOP.width}px; height: ${DESKTOP.height}px; ${artImage(this.art, "autoBetPanelBg")}">${this.content()}</div>`;
    }
    const v = this.view.design;
    const scale = fitScale(MOBILE, { maxWidth: this.isLandscape ? MOBILE.landscapeMaxWidth : MOBILE.maxWidth }, v);
    return html`
      <div class="backdrop"></div>
      <div class="panel art" data-part="panel" style="left: 50%; top: 50%; width: ${MOBILE.width}px; height: ${MOBILE.height}px; transform: translate(-50%, -50%) scale(${scale}); ${artImage(this.art, "autoBetPanelBg")}">
        ${this.content()}
        <button class="close art" type="button" data-part="close" style=${artImage(this.art, "closeRed")} @pointerdown=${pressAudio} @click=${() => this.bindings?.onClose()}></button>
      </div>
      ${nothing}
    `;
  }
}

export function defineAutoSpinPopupElement(): void {
  defineChromeElement(AUTOSPIN_POPUP_TAG, AutoSpinPopupElement);
}
